import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowClockwise, Translate } from "@phosphor-icons/react";

/**
 * v262 — Reusable translate-flags toolbar.
 *
 * Two modes :
 *
 * 1. DISPLAY mode (`text` + `onTranslated` OR uncontrolled) :
 *    Shows small flags. Clicking a flag calls the backend, replaces the text
 *    visually by the translation, and displays a « ↻ Original » button.
 *    Used above the ticket description and each comment.
 *
 * 2. COMPOSE mode (`getValue` + `setValue`) :
 *    Reads the current composer content, translates it, replaces the value.
 *    Used above the reply RichEditor to translate the drafted answer before
 *    sending.
 *
 * The component is INVISIBLE when the module is disabled globally OR no
 * language is active in the portal settings. Client role never renders it
 * because the parent gates it via `isStaff`.
 */
export default function TranslateFlags({
  // display mode
  text,
  onTranslated,      // (translatedText) => void — required if no internal state
  onRestore,         // () => void — optional callback when clicking Original
  // compose mode
  getValue,          // () => currentText
  setValue,          // (newText) => void
  // shared
  compact = false,
  className = "",
  label = null,      // string or null (null = no label, just flags)
  recommendedLang = null,  // ISO code to highlight as « client language »
}) {
  const [languages, setLanguages] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lastTranslation, setLastTranslation] = useState(null);   // for display mode Original button

  useEffect(() => {
    let cancelled = false;
    api.get("/translate/languages")
      .then((r) => {
        if (cancelled) return;
        setEnabled(!!r.data.enabled);
        setLanguages((r.data.languages || []).filter((l) => l.active));
      })
      .catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  if (!enabled || languages.length === 0) return null;

  const isCompose = typeof getValue === "function" && typeof setValue === "function";

  const translate = async (target) => {
    const source = isCompose ? (getValue() || "") : (text || "");
    const cleanText = source.replace(/<[^>]+>/g, "").trim();
    if (!cleanText) {
      toast.error("Rien à traduire — le champ est vide");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/translate", { text: source, target });
      const translated = r.data.translated;
      if (isCompose) {
        setValue(translated);
        toast.success(`Traduit en ${languages.find((l) => l.id === target)?.name || target}`);
      } else {
        setLastTranslation({ target, translated });
        onTranslated && onTranslated(translated);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la traduction");
    } finally {
      setBusy(false);
    }
  };

  const restore = () => {
    setLastTranslation(null);
    onRestore && onRestore();
  };

  // v272 — Mini rectangles (drapeaux réels). Passe de circle-flags à flagcdn.com
  // qui sert des PNG rectangulaires nets. Aspect ratio ~4:3.
  const COUNTRY = { fr: "fr", en: "gb", es: "es", de: "de", it: "it", pt: "pt", nl: "nl" };
  const flagSize = compact ? "w-4 h-[11px]" : "w-5 h-[13px]";
  return (
    <div
      className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}
      data-testid="translate-flags"
    >
      {label && (
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1 inline-flex items-center gap-1">
          <Translate size={11}/> {label}
        </span>
      )}
      {languages.map((l) => {
        const isRecommended = recommendedLang && l.id === recommendedLang;
        const isActive = lastTranslation?.target === l.id;
        const country = COUNTRY[l.id] || l.id;
        return (
        <button
          key={l.id}
          type="button"
          onClick={() => translate(l.id)}
          disabled={busy}
          className={`relative inline-flex items-center justify-center cursor-pointer select-none
            transition-transform duration-150 hover:scale-125
            disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
            ${isActive ? "drop-shadow-[0_0_4px_rgba(59,130,246,0.9)]" : ""}
            ${isRecommended ? "drop-shadow-[0_0_3px_rgba(16,185,129,0.95)]" : ""}`}
          title={isRecommended ? `Langue détectée du client — traduire en ${l.name}` : `Traduire en ${l.name}`}
          data-testid={`translate-flag-${l.id}${isRecommended ? "-recommended" : ""}`}
          aria-label={`Traduire en ${l.name}${isRecommended ? " (langue du client détectée)" : ""}`}
        >
          <img
            src={`https://flagcdn.com/w40/${country}.png`}
            srcSet={`https://flagcdn.com/w80/${country}.png 2x`}
            alt={l.name}
            loading="lazy"
            className={`${flagSize} object-cover rounded-[2px] shadow-sm`}
            aria-hidden
          />
          {isRecommended && (
            <span className="absolute -top-0.5 -right-1 w-1.5 h-1.5 rounded-full bg-emerald-500 ring-1 ring-background" aria-hidden/>
          )}
        </button>
        );
      })}
      {!isCompose && lastTranslation && (
        <button
          type="button"
          onClick={restore}
          className="ml-1 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
          data-testid="translate-restore"
          title="Afficher le texte original"
        >
          <ArrowClockwise size={11}/> Original
        </button>
      )}
      {busy && (
        <span
          className="inline-flex items-center gap-1.5 text-[11px] text-primary ml-1"
          data-testid="translate-busy"
        >
          <span className="inline-block w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin"/>
          Traduction en cours…
        </span>
      )}
    </div>
  );
}
